import { useId, useState, type ReactNode } from "react";

export type GuardianKey = "guardian_name" | "guardian_phone" | "guardian_email" | "guardian_relationship";

/** Caja 1 de «Mi hijo o hija»: los datos de quien inscribe (Eduardo
 * 2026-10-01). Con su correo se crea la Cuenta Familiar. Autocompletar del
 * navegador aquí es lo correcto: son los datos del papá o la mamá. */
export default function GuardianBox({
  values,
  onChange,
  showPhone,
  showRelationship,
  emailReadOnly = false,
  children,
}: {
  values: Record<GuardianKey, string | null | undefined>;
  onChange: (key: GuardianKey, value: string) => void;
  showPhone: boolean;
  showRelationship: boolean;
  /** Con sesión el correo es el de la cuenta y no se cambia aquí. */
  emailReadOnly?: boolean;
  /** Campos personalizados que el organizador puso en «Papá, mamá o tutor». */
  children?: ReactNode;
}) {
  const [explainOpen, setExplainOpen] = useState(false);
  const explainId = useId();
  return (
    <section className="public-registration-box public-registration-box--guardian" aria-labelledby="public-registration-guardian-box-title">
      <h3 className="public-registration-box__title" id="public-registration-guardian-box-title">
        <span className="public-registration-box__number" aria-hidden="true">1</span>
        <span>
          <span className="sr-only">1 · </span>Tus datos <small>(papá, mamá o tutor)</small>
        </span>
      </h3>
      <div className="public-registration-form-grid">
        <label className="public-registration-field">
          <span>Tu nombre</span>
          <input
            autoComplete="name"
            value={values.guardian_name ?? ""}
            onChange={(event) => onChange("guardian_name", event.target.value)}
          />
        </label>
        {showPhone ? (
          <label className="public-registration-field">
            <span>Tu celular</span>
            <input
              type="tel"
              autoComplete="tel"
              value={values.guardian_phone ?? ""}
              onChange={(event) => onChange("guardian_phone", event.target.value)}
            />
          </label>
        ) : null}
        <label className="public-registration-field">
          <span>Tu correo</span>
          <input
            type="email"
            autoComplete="email"
            inputMode="email"
            readOnly={emailReadOnly}
            aria-readonly={emailReadOnly || undefined}
            value={values.guardian_email ?? ""}
            onChange={(event) => onChange("guardian_email", event.target.value)}
          />
          {emailReadOnly ? <small className="public-registration-field__hint">Es el correo de tu cuenta.</small> : null}
        </label>
        {showRelationship ? (
          <label className="public-registration-field">
            <span>Parentesco</span>
            <input
              placeholder="Mamá, papá, tía…"
              value={values.guardian_relationship ?? ""}
              onChange={(event) => onChange("guardian_relationship", event.target.value)}
            />
          </label>
        ) : null}
        {children}
      </div>
      <p className="public-registration-box__note">
        Con tu correo creamos tu Cuenta Familiar: desde ahí ves y manejas a todos tus hijos.{" "}
        <button
          type="button"
          className="public-registration-box__toggle"
          aria-expanded={explainOpen}
          aria-controls={explainId}
          onClick={() => setExplainOpen((open) => !open)}
        >
          ¿Qué es?
        </button>
      </p>
      <ul className="public-registration-box__explain" id={explainId} hidden={!explainOpen}>
        <li>Es una sola cuenta con tu correo para todos tus hijos.</li>
        <li>Cambias de hijo arriba, en «Jugando como».</li>
        <li>Para inscribir a otro hijo, usa el mismo correo.</li>
      </ul>
    </section>
  );
}
